import React, {
  Fragment,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "./style.css";
import { API_URL } from "../../constant";
import {
  FaSearch,
  FaTimes,
  FaUsers,
  FaGlobe,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp,
  FaArrowRight,
} from "react-icons/fa";
import { GoArrowUpRight } from "react-icons/go";
import { debounce } from "lodash";

function BusinessAgentsHierarchyPage() {
  const [seniorAgents, setSeniorAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedTown, setSelectedTown] = useState("");
  const [selectedAdminArea, setSelectedAdminArea] = useState("");
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [availableTowns, setAvailableTowns] = useState([]);
  const [availableAdminAreas, setAvailableAdminAreas] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });

  const [expandedAgent, setExpandedAgent] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();

  // Create debounced search function
  const debouncedFetchAgents = useRef(
    debounce((page, filters) => {
      fetchSeniorAgents(page, filters);
    }, 500),
  ).current;

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sort agents to ensure TopCoin UK Business Master appears first
  const sortAgentsWithCharityFirst = (agents) => {
    if (!agents || agents.length === 0) return agents;

    return [...agents].sort((a, b) => {
      // Check if name contains "TopCoin UK Business Master"
      const aIsSpecial =
        a.name && a.name.includes("TopCoin UK Business Master");
      const bIsSpecial =
        b.name && b.name.includes("TopCoin UK Business Master");

      if (aIsSpecial && !bIsSpecial) return -1;
      if (!aIsSpecial && bIsSpecial) return 1;
      return 0;
    });
  };

  // Fetch senior agents with mini agents
  const fetchSeniorAgents = async (page = 1, filters = {}) => {
    setIsLoading(true);
    setError(null);
    setSearchError("");

    try {
      const params = new URLSearchParams({
        page: page,
        per_page: pagination.per_page,
      });

      // Add search if it has value
      if (filters.searchQuery && filters.searchQuery.trim()) {
        params.append("search", filters.searchQuery.trim());
      }

      // Add filters if they have values
      if (filters.selectedCountry && filters.selectedCountry.trim()) {
        params.append("country", filters.selectedCountry);
      }
      if (filters.selectedRegion && filters.selectedRegion.trim()) {
        params.append("region", filters.selectedRegion);
      }
      if (filters.selectedTown && filters.selectedTown.trim()) {
        params.append("town", filters.selectedTown);
      }
      if (filters.selectedAdminArea && filters.selectedAdminArea.trim()) {
        params.append("admin_area", filters.selectedAdminArea);
      }

      const response = await fetch(
        `${API_URL}/api/public/senior-business-agents-with-mini?${params}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Update filter options from response
        setAvailableCountries(data.filters?.available_countries || []);
        setAvailableRegions(data.filters?.available_regions || []);
        setAvailableTowns(data.filters?.available_towns || []);
        setAvailableAdminAreas(data.filters?.available_admin_areas || []);

        // Transform the data to match expected format
        let transformedAgents = (data.senior_agents || []).map((agent) => ({
          ...agent,
          total_mini_agents:
            agent.mini_agents_count ?? agent.mini_agents?.length ?? 0,
          total_agents: agent.agents_count ?? 0,
          total_retail_businesses: agent.retail_businesses_count ?? 0,
          mini_agents_introduced:
            agent.mini_agents?.map((mini) => ({
              id: mini.id,
              name: mini.name,
              representing_countries: mini.representing_countries || [],
              agent_code: mini.agent_code,
              agents_count: mini.agents_count ?? 0,
              retail_businesses_count: mini.retail_businesses_count ?? 0,
            })) || [],
        }));

        // Sort agents to show TopCoin UK Business Master first
        transformedAgents = sortAgentsWithCharityFirst(transformedAgents);

        setSeniorAgents(transformedAgents);
        setPagination({
          current_page: data.pagination.current_page,
          per_page: data.pagination.per_page,
          total: data.pagination.total,
          last_page: data.pagination.last_page,
        });
      } else {
        throw new Error(data.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching senior agents:", error);
      setError("Failed to load senior agents. Please try again later.");

      // Fallback data with TopCoin UK Business Master first
      const fallbackData = [
        {
          id: 2,
          name: "TopCoin UK Business Master",
          focus_countries: ["United Kingdom"],
          total_mini_agents: 4,
          total_agents: 4,
          total_retail_businesses: 0,
          coverage_areas: ["London", "Manchester", "Birmingham", "Leeds"],
          agent_code: "TCUK001",
          profile_photo: null,
          mini_agents_introduced: [
            {
              name: "John Smith",
              representing_countries: ["UK"],
              agent_code: "JS001",
            },
            {
              name: "Jane Doe",
              representing_countries: ["UK"],
              agent_code: "JD001",
            },
          ],
        },
        {
          id: 1,
          name: "Erambaamoorthy ILAMURUGAN",
          focus_countries: ["United Kingdom"],
          total_mini_agents: 2,
          total_agents: 2,
          total_retail_businesses: 0,
          coverage_areas: ["Area 1 – North London"],
          agent_code: "PB0VADQC",
          profile_photo: null,
          mini_agents_introduced: [
            {
              name: "Mini Agent 1",
              representing_countries: ["UK"],
              agent_code: "MA001",
            },
          ],
        },
      ];
      setSeniorAgents(sortAgentsWithCharityFirst(fallbackData));
      setAvailableCountries(["United Kingdom"]);
      setAvailableRegions(["South East", "North West", "Midlands"]);
      setAvailableTowns(["Town1", "Town2", "Town3", "Town4"]);
      setAvailableAdminAreas(["Area 1 – North London"]);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchSeniorAgents(1, {
      searchQuery: "",
      selectedCountry: "",
      selectedRegion: "",
      selectedTown: "",
      selectedAdminArea: "",
    });
  }, []);

  // Handle filter change - real-time updates
  const handleFilterChange = (filterType, value) => {
    let newSelectedCountry = selectedCountry;
    let newSelectedRegion = selectedRegion;
    let newSelectedTown = selectedTown;
    let newSelectedAdminArea = selectedAdminArea;

    switch (filterType) {
      case "country":
        newSelectedCountry = value;
        setSelectedCountry(value);
        break;
      case "region":
        newSelectedRegion = value;
        setSelectedRegion(value);
        break;
      case "town":
        newSelectedTown = value;
        setSelectedTown(value);
        break;
      case "adminArea":
        newSelectedAdminArea = value;
        setSelectedAdminArea(value);
        break;
      default:
        break;
    }

    // Debounce the API call with updated filters
    debouncedFetchAgents(1, {
      searchQuery,
      selectedCountry: newSelectedCountry,
      selectedRegion: newSelectedRegion,
      selectedTown: newSelectedTown,
      selectedAdminArea: newSelectedAdminArea,
    });
  };

  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    // Cancel any pending debounced calls and fetch immediately
    debouncedFetchAgents.cancel();
    fetchSeniorAgents(1, {
      searchQuery,
      selectedCountry,
      selectedRegion,
      selectedTown,
      selectedAdminArea,
    });
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCountry("");
    setSelectedRegion("");
    setSelectedTown("");
    setSelectedAdminArea("");
    fetchSeniorAgents(1, {
      searchQuery: "",
      selectedCountry: "",
      selectedRegion: "",
      selectedTown: "",
      selectedAdminArea: "",
    });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      fetchSeniorAgents(newPage, {
        searchQuery,
        selectedCountry,
        selectedRegion,
        selectedTown,
        selectedAdminArea,
      });
    }
  };

  // Toggle expand agent
  const toggleExpand = (agentId, totalMiniAgents) => {
    if (totalMiniAgents > 0) {
      if (expandedAgent === agentId) {
        setExpandedAgent(null);
      } else {
        setExpandedAgent(agentId);
      }
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchAgents.cancel();
    };
  }, [debouncedFetchAgents]);

  useEffect(() => {
    if (seniorAgents.length > 0 && !expandedAgent) {
      // Find the first agent with mini agents
      const firstAgentWithMini = seniorAgents.find(
        (agent) => agent.total_mini_agents > 0,
      );

      if (firstAgentWithMini) {
        setExpandedAgent(firstAgentWithMini.id);
      }
    }
  }, [seniorAgents]);

  // Get flag emoji for country
  const getFlagEmoji = (countryName) => {
    const flags = {
      UK: "🇬🇧",
      "United Kingdom": "🇬🇧",
      "Sri Lanka": "🇱🇰",
      Spain: "🇪🇸",
      Portugal: "🇵🇹",
      Ireland: "🇮🇪",
      France: "🇫🇷",
      Belgium: "🇧🇪",
      Iran: "🇮🇷",
      India: "🇮🇳",
      UAE: "🇦🇪",
      Oman: "🇴🇲",
      Nepal: "🇳🇵",
      Pakistan: "🇵🇰",
      Bangladesh: "🇧🇩",
      Austria: "🇦🇹",
      Japan: "🇯🇵",
      China: "🇨🇳",
      "South Korea": "🇰🇷",
      Mexico: "🇲🇽",
      Brazil: "🇧🇷",
      Argentina: "🇦🇷",
      Chile: "🇨🇱",
      "South Africa": "🇿🇦",
      Ghana: "🇬🇭",
      Kenya: "🇰🇪",
      Germany: "🇩🇪",
      Australia: "🇦🇺",
      Switzerland: "🇨🇭",
    };
    return flags[countryName] || "🌍";
  };

  return (
    <Fragment>
      <div
        className="index_ico page_wrapper"
        style={{ backgroundColor: "#f5f7fa", color: "#333" }}
      >
        <Header />
        <main className="page_content" style={{ paddingTop: "150px" }}>
          <div className="container py-4">
            {/* Header Section */}
            <div className="text-center mb-5">
              <h2
                className="text-center mb-3"
                style={{
                  fontWeight: "bold",
                  fontSize: "clamp(1.5rem, 4vw, 2.2rem)",
                  color: "#001964",
                  textShadow: "none",
                }}
              >
                Senior & Mini Business Agent Network
              </h2>
              <p
                className="text-center mb-4"
                style={{
                  color: "#666",
                  fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                  maxWidth: "700px",
                  margin: "0 auto",
                }}
              >
                Explore our network of Senior Agents and their introduced Mini
                Agents across different regions and countries.
              </p>
            </div>

            {/* Search and Filters Section */}
            <div
              className="mb-4"
              style={{
                borderRadius: "20px",
                padding: isMobile ? "20px" : "30px",
              }}
            >
              {/* Search Bar - Full width on mobile */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  marginBottom: "20px",
                  padding: isMobile ? "0 10px" : "0",
                }}
              >
                <form
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "white",
                    border: "1px solid #d0d0d0",
                    borderRadius: "32px",
                    width: "100%",
                    maxWidth: isMobile ? "100%" : "500px",
                  }}
                  onSubmit={handleSearch}
                >
                  <span style={{ padding: "0 12px", color: "#666" }}>
                    <FaSearch size={isMobile ? 14 : 16} />
                  </span>
                  <input
                    type="text"
                    placeholder={
                      isMobile
                        ? "Search agents by name, ID, country, region, or town..."
                        : "Search agents by name, ID, country, region, or town..."
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      fontSize: isMobile ? "14px" : "14px",
                      color: "#333",
                      backgroundColor: "transparent",
                      padding: isMobile ? "10px 0" : "12px 0",
                      minHeight: isMobile ? "40px" : "auto",
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery("");
                        fetchSeniorAgents(1, {
                          searchQuery: "",
                          selectedCountry,
                          selectedRegion,
                          selectedTown,
                          selectedAdminArea,
                        });
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        padding: "0 8px",
                        color: "#666",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Clear search"
                    >
                      <FaTimes size={isMobile ? 14 : 16} />
                    </button>
                  )}
                  <button
                    type="submit"
                    style={{
                      backgroundColor: "#2D4CA8",
                      color: "white",
                      border: "none",
                      borderRadius: "0 32px 32px 0",
                      padding: isMobile ? "10px 16px" : "12px 24px",
                      fontWeight: "bold",
                      fontSize: isMobile ? "13px" : "14px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      minHeight: isMobile ? "40px" : "auto",
                    }}
                  >
                    {isMobile ? "Search" : "Search"}
                  </button>
                </form>
              </div>

              {/* Filter Dropdowns - Stack vertically on mobile */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: "16px",
                  justifyContent: "center",
                  flexDirection: isMobile ? "column" : "row",
                  padding: isMobile ? "0 10px" : "0",
                }}
              >
                {/* Country Dropdown */}
                <div
                  style={{
                    position: "relative",
                    width: isMobile ? "100%" : "auto",
                    minWidth: isMobile ? "100%" : "140px",
                    flex: isMobile ? "none" : "1 1 auto",
                    maxWidth: isMobile ? "100%" : "200px",
                  }}
                >
                  <select
                    value={selectedCountry}
                    onChange={(e) =>
                      handleFilterChange("country", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: isMobile
                        ? "14px 32px 5px 12px"
                        : "12px 32px 12px 12px",
                      borderRadius: "8px",
                      border: "1px solid #d0d0d0",
                      backgroundColor: "white",
                      fontSize: isMobile ? "14px" : "14px",
                      color: "black",
                      fontWeight: "500",
                      appearance: "none",
                      cursor: "pointer",
                      minHeight: isMobile ? "48px" : "auto",
                    }}
                  >
                    <option value="">Country</option>
                    {availableCountries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  <FaChevronDown
                    size={isMobile ? 16 : 14}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "black",
                      pointerEvents: "none",
                    }}
                  />
                </div>

                {/* Admin Area Dropdown */}
                <div
                  style={{
                    position: "relative",
                    width: isMobile ? "100%" : "auto",
                    minWidth: isMobile ? "100%" : "140px",
                    flex: isMobile ? "none" : "1 1 auto",
                    maxWidth: isMobile ? "100%" : "200px",
                  }}
                >
                  <select
                    value={selectedAdminArea}
                    onChange={(e) =>
                      handleFilterChange("adminArea", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: isMobile
                        ? "14px 32px 14px 12px"
                        : "12px 32px 12px 12px",
                      borderRadius: "8px",
                      border: "1px solid #d0d0d0",
                      backgroundColor: "white",
                      fontSize: isMobile ? "14px" : "14px",
                      color: "black",
                      fontWeight: "500",
                      appearance: "none",
                      cursor: "pointer",
                      minHeight: isMobile ? "48px" : "auto",
                    }}
                  >
                    <option value="">Admin Area</option>
                    {availableAdminAreas.map((area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                  <FaChevronDown
                    size={isMobile ? 16 : 14}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "black",
                      pointerEvents: "none",
                    }}
                  />
                </div>

                {/* Town Dropdown */}
                <div
                  style={{
                    position: "relative",
                    width: isMobile ? "100%" : "auto",
                    minWidth: isMobile ? "100%" : "140px",
                    flex: isMobile ? "none" : "1 1 auto",
                    maxWidth: isMobile ? "100%" : "200px",
                  }}
                >
                  <select
                    value={selectedTown}
                    onChange={(e) => handleFilterChange("town", e.target.value)}
                    style={{
                      width: "100%",
                      padding: isMobile
                        ? "14px 32px 14px 12px"
                        : "12px 32px 12px 12px",
                      borderRadius: "8px",
                      border: "1px solid #d0d0d0",
                      backgroundColor: "white",
                      fontSize: isMobile ? "14px" : "14px",
                      color: "black",
                      fontWeight: "500",
                      appearance: "none",
                      cursor: "pointer",
                      minHeight: isMobile ? "48px" : "auto",
                    }}
                  >
                    <option value="">Town</option>
                    {availableTowns.map((town) => (
                      <option key={town} value={town}>
                        {town}
                      </option>
                    ))}
                  </select>
                  <FaChevronDown
                    size={isMobile ? 16 : 14}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "black",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </div>

              {searchError && (
                <div
                  style={{
                    color: "#ff6b6b",
                    fontSize: isMobile ? "13px" : "14px",
                    marginTop: "8px",
                    textAlign: "center",
                    padding: "0 10px",
                  }}
                >
                  {searchError}
                </div>
              )}
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-5">
                <div
                  className="spinner-border"
                  style={{ color: "#2D4CA8" }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div
                className="text-center py-5"
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  padding: "40px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <p className="mb-3" style={{ color: "#dc3545" }}>
                  {error}
                </p>
                <button
                  className="btn"
                  onClick={() =>
                    fetchSeniorAgents(1, {
                      searchQuery,
                      selectedCountry,
                      selectedRegion,
                      selectedTown,
                      selectedAdminArea,
                    })
                  }
                  style={{
                    backgroundColor: "#2D4CA8",
                    color: "white",
                    border: "none",
                    padding: "10px 24px",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Senior Agents List */}
            {!isLoading && !error && (
              <>
                <div className="row">
                  {seniorAgents.length === 0 ? (
                    <div className="col-12">
                      <div
                        className="text-center py-5"
                        style={{
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          border: "1px solid #e9ecef",
                        }}
                      >
                        <FaUsers
                          size={48}
                          style={{ color: "#ccc", marginBottom: "16px" }}
                        />
                        <h4 style={{ color: "#666", marginBottom: "8px" }}>
                          No Senior Agents Found
                        </h4>
                        <p style={{ color: "#999" }}>
                          Try adjusting your search filters
                        </p>
                        <button
                          onClick={handleResetFilters}
                          style={{
                            backgroundColor: "transparent",
                            color: "#2D4CA8",
                            border: "1px solid #2D4CA8",
                            borderRadius: "20px",
                            padding: "8px 20px",
                            fontSize: "0.95rem",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#2D4CA8";
                            e.target.style.color = "white";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent";
                            e.target.style.color = "#2D4CA8";
                          }}
                        >
                          Show All Agents
                        </button>
                      </div>
                    </div>
                  ) : (
                    seniorAgents.map((agent, index) => (
                      <div key={agent.id || index} className="col-12 mb-3">
                        <div
                          className="card border-0 rounded-3 overflow-hidden"
                          style={{
                            backgroundColor: "#ffffff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            transition: "all 0.3s ease",
                            border: "1px solid #e9ecef",
                          }}
                        >
                          {/* Senior Agent Card */}
                          <div
                            className="card-header p-3 d-flex justify-content-between align-items-center"
                            style={{
                              backgroundColor: "#ffffff",
                              borderBottom: "1px solid #e9ecef",
                              cursor:
                                agent.total_mini_agents > 0
                                  ? "pointer"
                                  : "default",
                            }}
                            onClick={() =>
                              toggleExpand(
                                agent.id || index,
                                agent.total_mini_agents,
                              )
                            }
                          >
                            <div
                              className="d-flex align-items-center gap-3"
                              style={{ flex: 1 }}
                            >
                              <div style={{ flex: 1 }}>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <h6
                                    className="mb-0 fw-bold"
                                    style={{
                                      color: "#001964",
                                      fontSize: isMobile ? "1rem" : "1.1rem",
                                    }}
                                  >
                                    {agent.name} ({agent.id}){" "}
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "#2D4CA8",
                                        fontWeight: "500",
                                        backgroundColor: "#e6f0ff",
                                        padding: "2px 8px",
                                        borderRadius: "12px",
                                      }}
                                    >
                                      Senior Agent
                                    </span>
                                  </h6>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                    marginTop: "4px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#666",
                                    }}
                                  >
                                    <FaUsers
                                      className="me-1"
                                      style={{
                                        fontSize: "0.7rem",
                                        color: "#2D4CA8",
                                      }}
                                    />
                                    {agent.total_agents}{" "}
                                    {agent.total_agents !== 1
                                      ? "Agents"
                                      : "Agent"}
                                    {" · "}
                                    {agent.total_retail_businesses}{" "}
                                    {agent.total_retail_businesses !== 1
                                      ? "Retail Businesses"
                                      : "Retail Business"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="d-flex align-items-center gap-3">
                              {/* Join Under Button for Senior Agent */}
                              <a
                                href={`/retail-business-signup?ref=${agent.agent_code}`}
                                style={{
                                  backgroundColor: "#223e9a",
                                  color: "white",
                                  padding: isMobile ? "8px 12px" : "6px 16px",
                                  borderRadius: "4px",
                                  textDecoration: "none",
                                  fontSize: isMobile
                                    ? "12px"
                                    : "clamp(0.8rem, 2vw, 0.9rem)",
                                  fontWeight: "500",
                                  display: "inline-block",
                                  transition: "all 0.2s ease",
                                  whiteSpace: "nowrap",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "#1a3a8f";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "#223e9a";
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                Join Under
                              </a>

                              {/* Expand/Collapse Icon */}
                              {agent.total_mini_agents > -1 && (
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    backgroundColor: "#f0f0f0",
                                    color: "#2D4CA8",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  {expandedAgent === (agent.id || index) ? (
                                    <FaChevronUp size={14} />
                                  ) : (
                                    <FaChevronDown size={14} />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Mini Agents List  */}
                          {expandedAgent === (agent.id || index) &&
                            agent.total_mini_agents > 0 && (
                              <div
                                className="card-body p-3"
                                style={{ backgroundColor: "#fafbfd" }}
                              >
                                {/* Simple List View with Join Under Buttons */}
                                <div
                                  className="mini-agents-list"
                                  style={{
                                    borderRadius: "8px",
                                    border: "1px solid #e9ecef",
                                    backgroundColor: "#ffffff",
                                    overflow: "hidden",
                                  }}
                                >
                                  <table
                                    className="mini-agents-table"
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                    }}
                                  >
                                    <thead>
                                      <tr
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          borderBottom: "1px solid #e0e0e0",
                                        }}
                                      >
                                        <th
                                          style={{
                                            textAlign: "center",
                                            padding: "12px 8px",
                                            fontWeight: "600",
                                            color: "#001964",
                                            fontSize: isMobile
                                              ? "12px"
                                              : "13px",
                                            width: "60px",
                                          }}
                                        ></th>
                                        <th
                                          style={{
                                            textAlign: "left",
                                            padding: "12px 8px",
                                            fontWeight: "600",
                                            color: "#001964",
                                            fontSize: isMobile
                                              ? "12px"
                                              : "13px",
                                          }}
                                        >
                                          Mini Agents
                                        </th>
                                        {/* <th
                                          style={{
                                            textAlign: "center",
                                            padding: "12px 8px",
                                            fontWeight: "600",
                                            color: "#001964",
                                            fontSize: isMobile
                                              ? "12px"
                                              : "13px",
                                            width: isMobile ? "100px" : "140px",
                                          }}
                                        >
                                          Counts
                                        </th> */}
                                        <th
                                          style={{
                                            textAlign: "center",
                                            padding: "12px 8px",
                                            fontWeight: "600",
                                            color: "#001964",
                                            fontSize: isMobile
                                              ? "12px"
                                              : "13px",
                                            width: isMobile ? "100px" : "140px",
                                          }}
                                        ></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {agent.mini_agents_introduced?.map(
                                        (mini, idx) => (
                                          <tr
                                            key={idx}
                                            style={{
                                              transition:
                                                "background-color 0.2s ease",
                                              borderBottom:
                                                idx !==
                                                agent.mini_agents_introduced
                                                  .length -
                                                  1
                                                  ? "1px solid #e9ecef"
                                                  : "none",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.backgroundColor =
                                                "#f8f9fa";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.backgroundColor =
                                                "transparent";
                                            }}
                                          >
                                            <td
                                              style={{
                                                textAlign: "center",
                                                padding: "12px 8px",
                                                fontSize: isMobile
                                                  ? "11px"
                                                  : "12px",
                                                color: "#666",
                                              }}
                                            >
                                              {idx + 1}
                                            </td>
                                            <td
                                              style={{
                                                textAlign: "left",
                                                padding: "12px 8px",
                                                fontSize: isMobile
                                                  ? "13px"
                                                  : "14px",
                                                fontWeight: "500",
                                                color: "#333",
                                              }}
                                            >
                                              <div className="d-flex align-items-center gap-2">
                                                <span>
                                                  {mini.name || "Unknown"}
                                                  <span
                                                    style={{
                                                      fontSize: isMobile
                                                        ? "11px"
                                                        : "12px",
                                                      color: "#666",
                                                      fontWeight: "normal",
                                                      marginLeft: "4px",
                                                    }}
                                                  >
                                                    ({mini.id || "N/A"})
                                                  </span>
                                                </span>
                                              </div>
                                            </td>
                                            {/* <td
                                              style={{
                                                textAlign: "center",
                                                padding: "12px 8px",
                                                fontSize: isMobile
                                                  ? "11px"
                                                  : "12px",
                                                color: "#666",
                                              }}
                                            >
                                              {(mini.agents_count ?? 0) > 0 && (
                                                <>
                                                  {mini.agents_count}{" "}
                                                  {mini.agents_count !== 1
                                                    ? "Agents"
                                                    : "Agent"}
                                                  {" · "}
                                                </>
                                              )}
                                              {mini.retail_businesses_count ?? 0}{" "}
                                              {(mini.retail_businesses_count ??
                                                0) !== 1
                                                ? "Retail Businesses"
                                                : "Retail Business"}
                                            </td> */}
                                            <td
                                              style={{
                                                textAlign: "center",
                                                padding: "12px 8px",
                                              }}
                                            >
                                              <a
                                                href={`/retail-business-signup?ref=${mini.agent_code || agent.agent_code}`}
                                                style={{
                                                  backgroundColor: "#223e9a",
                                                  color: "white",
                                                  padding: isMobile
                                                    ? "6px 12px"
                                                    : "6px 16px",
                                                  borderRadius: "4px",
                                                  textDecoration: "none",
                                                  fontSize: isMobile
                                                    ? "12px"
                                                    : "clamp(0.8rem, 2vw, 0.9rem)",
                                                  fontWeight: "500",
                                                  display: "inline-block",
                                                  transition: "all 0.2s ease",
                                                  whiteSpace: "nowrap",
                                                  border: "none",
                                                  cursor: "pointer",
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.backgroundColor =
                                                    "#1a3a8f";
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.backgroundColor =
                                                    "#223e9a";
                                                }}
                                              >
                                                Join Under
                                              </a>
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                </div>

                                {agent.mini_agents_introduced?.length === 0 && (
                                  <div className="text-center py-4">
                                    <p
                                      style={{
                                        color: "#999",
                                        fontSize: "13px",
                                      }}
                                    >
                                      No mini agents introduced yet
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: "20px",
                      gap: isMobile ? "5px" : "10px",
                      flexWrap: "wrap",
                      padding: "0 10px",
                    }}
                  >
                    <button
                      onClick={() =>
                        handlePageChange(pagination.current_page - 1)
                      }
                      disabled={pagination.current_page === 1}
                      style={{
                        padding: isMobile ? "8px 12px" : "8px 16px",
                        backgroundColor:
                          pagination.current_page === 1 ? "#cccccc" : "#2D4CA8",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor:
                          pagination.current_page === 1
                            ? "not-allowed"
                            : "pointer",
                        fontSize: isMobile
                          ? "13px"
                          : "clamp(0.8rem, 2vw, 0.9rem)",
                        minWidth: isMobile ? "70px" : "auto",
                      }}
                    >
                      Prev
                    </button>

                    <span
                      style={{
                        padding: isMobile ? "8px 8px" : "8px 16px",
                        color: "#333",
                        fontSize: isMobile
                          ? "13px"
                          : "clamp(0.8rem, 2vw, 0.9rem)",
                      }}
                    >
                      Page {pagination.current_page} of {pagination.last_page}
                    </span>

                    <button
                      onClick={() =>
                        handlePageChange(pagination.current_page + 1)
                      }
                      disabled={
                        pagination.current_page === pagination.last_page
                      }
                      style={{
                        padding: isMobile ? "8px 12px" : "8px 16px",
                        backgroundColor:
                          pagination.current_page === pagination.last_page
                            ? "#cccccc"
                            : "#2D4CA8",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor:
                          pagination.current_page === pagination.last_page
                            ? "not-allowed"
                            : "pointer",
                        fontSize: isMobile
                          ? "13px"
                          : "clamp(0.8rem, 2vw, 0.9rem)",
                        minWidth: isMobile ? "70px" : "auto",
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
        <Footer />
      </div>

      <style>
        {`
          .page-link {
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .page-link:hover {
            background-color: #f0f3ff;
            color: #2D4CA8;
          }
          .page-item.active .page-link {
            background-color: #2D4CA8;
            border-color: #2D4CA8;
          }
          .card {
            transition: all 0.3s ease;
          }
          .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          }
          
          /* Table scrollbar styling */
          .table-container::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          .table-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          
          .table-container::-webkit-scrollbar-thumb {
            background: #2D4CA8;
            border-radius: 10px;
          }
          
          .table-container::-webkit-scrollbar-thumb:hover {
            background: #1a3a8f;
          }
          
          @media (max-width: 768px) {
            .card-header {
              padding: 12px !important;
            }
            .card-body {
              padding: 12px !important;
            }
            
            .mini-agents-table th,
            .mini-agents-table td {
              padding: 8px 4px !important;
            }
          }
          
          select, input {
            -webkit-appearance: none;
          }
          
          select:focus, input:focus {
            outline: none;
          }
        `}
      </style>
    </Fragment>
  );
}

export default BusinessAgentsHierarchyPage;
